import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { ListProductSizesParams, ProductService, ProductSizeItem } from '@app/services/product.service';
import { ProductSelectionService } from '@app/services/product-selection.service';

@Component({
    selector: 'tt-select-prodsize',
    templateUrl: './select-prodsize.component.html',
    styleUrls: ['./select-prodsize.component.css'],
    standalone: false
})
export class SelectProdsizeComponent implements OnInit, OnDestroy {
    public items: ProductSizeItem[] = [];

    public searchText = '';

    public loading = false;

    public error = '';

    private params: Omit<ListProductSizesParams, 'prodsize_name'> = {
        webpage_name: '',
        parm1: '',
        parm2: '',
        prod_id: ''
    };

    private pendingLoads = 0;

    private readonly searchChanges = new Subject<string>();

    private readonly destroyed = new Subject<void>();

    constructor(
        private product: ProductService,
        private selection: ProductSelectionService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator
    ) {
        this.navigator = useNavigatorFor(navigator, this);

        this.searchChanges.pipe(debounceTime(500), takeUntil(this.destroyed)).subscribe(value => {
            if (value.length >= 3) void this.loadSizes();
        });
    }

    public async ngOnInit(): Promise<void> {
        const params = this.navigator?.params;

        this.params = {
            webpage_name: String(params?.['webpage_name'] ?? ''),
            parm1: String(params?.['parm1'] ?? ''),
            parm2: String(params?.['parm2'] ?? ''),
            prod_id: String(params?.['prod_id'] ?? '')
        };

        await this.loadSizes();
    }

    public changeSearch(value: string): void {
        this.searchText = value;
        this.searchChanges.next(value);
    }

    public search(): void {
        if (!this.loading) void this.loadSizes();
    }

    public async loadSizes(): Promise<void> {
        this.loading = true;
        ++this.pendingLoads;
        this.error = '';

        try {
            this.items = await this.product.listProductSizes({
                prodsize_name: this.searchText,
                ...this.params
            });
        } catch (error) {
            this.error = error instanceof Error ? error.message : String(error);
        } finally {
            this.loading = --this.pendingLoads > 0;
        }
    }

    public select(item: ProductSizeItem): void {
        if (item.item_id === '') return;

        this.selection.selectSize(item.item_id);
        this.navigator?.back();
    }

    public ngOnDestroy(): void {
        this.destroyed.next();
        this.destroyed.complete();
    }
}
